<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <form-card class="mt-7">
        <template #title>
          {{ $t(`pages.campaignList.modal.general.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignList.modal.general.description`) }}
        </template>
        <multi-select
          v-model="rawCampaign.region"
          :error="errors?.region"
          :searchable="true"
          :label="$t('campaign.region')"
          :placeholder="$t('campaign.region')"
          :options="fetchRegions"
          class="my-4 !w-full"
        />
        <Switch v-model="rawCampaign.active" :label="$t('campaign.active')" />
      </form-card>
      <form-card>
        <template #title>
          {{ $t(`pages.campaignList.modal.translated.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignList.modal.translated.description`) }}
        </template>
        <translation-accordion v-slot="{ translation }" v-model="rawCampaign" :always-show-all-languages="true">
          <LockedComponent v-model="rawCampaign.locked.translations[translation.language]">
            <template #default>
              <div v-if="getDefaultTranslation(translation.language)" class="flex flex-col gap-y-5">
                <InputField
                  :model-value="getDefaultTranslation(translation.language)?.title"
                  type="text"
                  :label="$t('campaign.translation.title')"
                  disabled
                />
                <div>
                  <div class="font-bold pb-2">
                    {{ $t('campaign.translation.description.DEFAULT') }}
                  </div>
                  <div class="vhtml border border-light-gray p-3 min-h-[10rem]" v-html="getDefaultTranslation(translation.language)?.text" />
                </div>
              </div>
              <div v-else>
                {{ $t('pages.campaignList.modal.translated.noTranslation') }}
              </div>
            </template>
            <template #modelValue>
              <div class="flex flex-col gap-y-5">
                <InputField
                  v-model="translation.title"
                  type="text"
                  name="title"
                  :error="errors?.title"
                  :label="$t('campaign.translation.title')"
                />
                <HtmlEditor
                  v-model="translation.text"
                  name="text"
                  :error="errors?.text"
                  :label="$t('campaign.translation.description.DEFAULT')"
                />
              </div>
            </template>
          </LockedComponent>
        </translation-accordion>
      </form-card>
      <form-card>
        <template #title>
          {{ $t(`pages.campaignList.modal.furtherInformation.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignList.modal.furtherInformation.description`) }}
        </template>
        <div class="flex flex-col gap-y-4">
          <LockedComponent v-model="rawCampaign.locked.driversCount">
            <template #default>
              <InputField
                :model-value="campaignGroup.defaultDriversCount"
                type="number"
                :label="$t('campaign.driversCount')"
                :placeholder="$t('campaign.driversCount')"
                class="w-1/2"
                disabled
              />
            </template>
            <template #modelValue>
              <InputField
                v-model="rawCampaign.driversCount"
                type="number"
                name="defaultDriversCount"
                :error="errors?.driversCount"
                :label="$t('campaign.driversCount')"
                :placeholder="$t('campaign.driversCount')"
                class="w-1/2"
              />
            </template>
          </LockedComponent>
          <LockedComponent v-model="rawCampaign.locked.date">
            <template #default>
              <DateRange
                mode="datetime"
                :model-value="{
                  start: campaignGroup.defaultStartDate,
                  end: campaignGroup.defaultEndDate,
                }"
                :default-value="{
                  start: campaignGroup.defaultStartDate,
                  end: campaignGroup.defaultEndDate,
                }"
                :label="$t('campaign.dateRange')"
                :placeholder="$t('campaign.dateRange')"
                disabled
              />
            </template>
            <template #modelValue>
              <DateRange
                v-model="rawCampaign.date"
                mode="datetime"
                :default-value="{
                  start: campaignGroup.defaultStartDate,
                  end: campaignGroup.defaultEndDate,
                }"
                :errors="errors?.date"
                :label="$t('campaign.dateRange')"
                :placeholder="$t('campaign.dateRange')"
              />
            </template>
          </LockedComponent>
        </div>
      </form-card>
      <form-card>
        <template #title>
          {{ $t(`pages.campaignList.modal.campaignPictures.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignList.modal.campaignPictures.description`) }}
        </template>
        <div class="overflow-x-auto snap-x py-5 px-1">
          <div class="w-[250%] flex gap-x-5">
            <box
              v-for="picture in pictures"
              :key="picture.name"
              :aspect="1.3"
              class="snap-start w-1/2 srink-0"
            >
              <LockedComponent v-model="rawCampaign.locked.pictures[picture.key]" :vertical="true">
                <template #default>
                  <Image
                    :file="defaultPictureBlobs[picture.key]"
                    class="h-full defaultPictures"
                    :alt="picture.name"
                    :icon="PhotoIcon"
                    :aspect="1"
                    disabled
                  />
                </template>
                <template #modelValue>
                  <medium
                    v-model="picture.upload.file"
                    class="h-full p-0"
                    :label="picture.name"
                    :icon="PhotoIcon"
                    :aspect="1"
                    @click="picture.upload.open"
                  />
                </template>
              </LockedComponent>
            </box>
          </div>
        </div>
      </form-card>
      <form-card>
        <template #title>
          {{ $t(`pages.campaignList.modal.morePictures.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignList.modal.morePictures.description`) }}
        </template>
        <Images
          v-model="images"
          :aspect="0.5"
          :label="$t('campaignGroup.uploadPictures')"
          @choose-file="openImageChooser"
        />
      </form-card>
      <form-card>
        <template #title>
          {{ $t(`pages.campaignList.modal.driverFilter.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignList.modal.driverFilter.description`) }}
          <CheckFilter
            :filter="
              rawCampaign.locked.qualifiedDriversFilter
                ? campaignGroup.defaultQualifiedDriversFilter
                : rawCampaign.qualifiedDriversFilter
            "
            :validation-function="validationFunction"
            route="/filters/campaign-driver"
            class="mt-4"
            :label="$t('pages.campaignList.modal.driverFilter.countLabel')"
            :additional-payload="{
              startDate: rawCampaign.locked.date ? campaignGroup?.defaultStartDate : rawCampaign.date.start,
              endDate: rawCampaign.locked.date ? campaignGroup?.defaultEndDate : rawCampaign.date.end,
              region: rawCampaign.region?.split('/').pop(),
            }"
          />
        </template>
        <LockedComponent v-model="rawCampaign.locked.qualifiedDriversFilter">
          <template #default>
            <LogicGate
              ref="driverFilter"
              :model-value="campaignGroup.defaultQualifiedDriversFilter"
              route="/filters/campaign-driver"
              disabled
            />
          </template>
          <template #modelValue>
            <LogicGate
              ref="driverFilter"
              v-model="rawCampaign.qualifiedDriversFilter"
              route="/filters/campaign-driver"
            />
          </template>
        </LockedComponent>
      </form-card>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, type Ref, watch,
} from 'vue';
import MultiSelect from '@utils/Multiselect.vue';
import Modal from '@utils/Modal/Modal.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import InputField from '@utils/inputs/Input.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import Medium from '@utils/inputs/Medium.vue';
import Box from '@utils/Box.vue';
import TranslationAccordion from '@utils/language/TranslationAccordion.vue';
import PhotoIcon from '@images/photo.svg';
import LogicGate from '@utils/logicGate/LogicGate.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import { useVModel } from '@vueuse/core';
import { useValidation } from '@hooks/form';
import {
  array, boolean, date, number, object, string,
} from 'yup';
import { useI18n } from 'vue-i18n';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import DateRange from '@utils/inputs/DateRange.vue';
import Switch from '@utils/inputs/Switch.vue';
import { syncRef } from '@hooks/state';
import { Upload, useFileUpload, useSingleFileUpload } from '@utils/upload/fileUpload';
import LockedComponent from '@utils/LockedComponent.vue';
import { useBlob } from '@hooks/file';
import CheckFilter from '@utils/logicGate/CheckFilter.vue';
import { parseFilter } from '@utils/logicGate/logicFilter';
import HtmlEditor from '@utils/inputs/HtmlEditor.vue';
import type {
  Campaign,
  CampaignGroup,
  CampaignTranslations,
  Filter,
  MaybeHydra,
  Region,
  WbFile,
} from '@/entities/Entities';
import Images from '@/components/utils/inputs/Images.vue';

interface Props {
  open: boolean;
  campaign?: Campaign;
  campaignGroup?: CampaignGroup;
}

const { t, locale } = useI18n();

const notify = useNotify();

const emit = defineEmits(['update:open', 'update:campaign', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalCampaign = useVModel(props, 'campaign', emit);

const isNew = computed(() => !props.campaign);
const tKey = computed(() =>
  (isNew.value ? 'pages.campaignList.modal.new' : 'pages.campaignList.modal.edit'));

const initialData = (campaign?: Campaign) =>
  (campaign
    ? {
      ...campaign,
      translations: [
        ...campaign.translations,
      ],
      date: {
        start: campaign.startDate ? new Date(campaign?.startDate) : new Date(),
        end: campaign.endDate ? new Date(campaign?.endDate) : new Date(),
      },
      qualifiedDriversFilter: campaign.qualifiedDriversFilter ?? {
        logicGroups: [],
      },
      region: campaign.region?.['@id'] ?? undefined,
      locked: {
        date: campaign.startDate == null || campaign.endDate == null,
        driversCount: campaign.driversCount == null,
        translations: {
          en: campaign.translations.find((trans) => trans.language === 'en') === undefined,
          de: campaign.translations.find((trans) => trans.language === 'de') === undefined,
          fr: campaign.translations.find((trans) => trans.language === 'fr') === undefined,
          it: campaign.translations.find((trans) => trans.language === 'it') === undefined,
        },
        qualifiedDriversFilter: campaign.qualifiedDriversFilter == null,
        pictures: {
          pictureLeft: campaign.pictureLeft == null,
          pictureRight: campaign.pictureRight == null,
          pictureTop: campaign.pictureTop == null,
          pictureFront: campaign.pictureFront == null,
          pictureBack: campaign.pictureBack == null,
        },
      },
    }
    : {
      active: false,
      region: undefined,
      date: {
        start: new Date(),
        end: new Date(),
      },
      driversCount: undefined,
      translations: [{ title: undefined, text: undefined, language: locale.value }],
      qualifiedDriversFilter: {
        logicGroups: [],
      },
      locked: {
        date: true,
        driversCount: true,
        translations: {
          en: true,
          de: true,
          fr: true,
          it: true,
        },
        pictures: {
          pictureLeft: true,
          pictureRight: true,
          pictureTop: true,
          pictureFront: true,
          pictureBack: true,
        },
        qualifiedDriversFilter: true,
      },
    });

const getDefaultTranslation = (lang: string) =>
  props.campaignGroup?.translations.find((el) => el.language === lang);

const defaultPictureBlobs = ref<{ [T: string]: Ref<Blob | undefined> }>({});

const unwatchOpen = watch(
  () => props.open,
  (newValue: boolean) => {
    if (!newValue || !props.campaignGroup) {
      return;
    }
    unwatchOpen();
    if (props.campaignGroup.defaultPictureLeft) {
      const { blob: srcBlob } = useBlob(
        computed(() => `/files/${props.campaignGroup.defaultPictureLeft.id}/content`),
      );
      defaultPictureBlobs.value.pictureLeft = srcBlob;
    }
    if (props.campaignGroup.defaultPictureRight) {
      const { blob: srcBlob } = useBlob(
        computed(() => `/files/${props.campaignGroup.defaultPictureRight.id}/content`),
      );
      defaultPictureBlobs.value.pictureRight = srcBlob;
    }
    if (props.campaignGroup.defaultPictureTop) {
      const { blob: srcBlob } = useBlob(
        computed(() => `/files/${props.campaignGroup.defaultPictureTop.id}/content`),
      );
      defaultPictureBlobs.value.pictureTop = srcBlob;
    }
    if (props.campaignGroup.defaultPictureBack) {
      const { blob: srcBlob } = useBlob(
        computed(() => `/files/${props.campaignGroup.defaultPictureBack.id}/content`),
      );
      defaultPictureBlobs.value.pictureBack = srcBlob;
    }
    if (props.campaignGroup.defaultPictureFront) {
      const { blob: srcBlob } = useBlob(
        computed(() => `/files/${props.campaignGroup.defaultPictureFront.id}/content`),
      );
      defaultPictureBlobs.value.pictureFront = srcBlob;
    }
  },
);

const prevFileLeft = computed(() =>
  (props.campaign && props.campaign.pictureLeft ? Upload.from(props.campaign.pictureLeft) : undefined));
const prevFileRight = computed(() =>
  (props.campaign && props.campaign.pictureRight
    ? Upload.from(props.campaign.pictureRight)
    : undefined));
const prevFileTop = computed(() =>
  (props.campaign && props.campaign.pictureTop ? Upload.from(props.campaign.pictureTop) : undefined));
const prevFileBack = computed(() =>
  (props.campaign && props.campaign.pictureBack ? Upload.from(props.campaign.pictureBack) : undefined));
const prevFileFront = computed(() =>
  (props.campaign && props.campaign.pictureFront
    ? Upload.from(props.campaign.pictureFront)
    : undefined));

const {
  files: images,
  open: openImageChooser,
  uploadDiff: uploadImageDiff,
  loading: imagesSaving,
  getUpdatedFiles,
  reset: resetImages,
} = useFileUpload<WbFile>(
  computed(
    () =>
      props.campaign?.additionalPhotos
        ?.filter((photo) => photo.file)
        ?.map((photo) => Upload.from(photo.file)) ?? [],
  ),
);

const pictures = reactive([
  {
    key: 'pictureLeft',
    defaultKey: 'defaultPictureLeft',
    name: t('pages.campaignList.modal.campaignPictures.pictures.left'),
    upload: useSingleFileUpload<WbFile>(prevFileLeft),
  },
  {
    key: 'pictureRight',
    defaultKey: 'defaultPictureRight',
    name: t('pages.campaignList.modal.campaignPictures.pictures.right'),
    upload: useSingleFileUpload<WbFile>(prevFileRight),
  },
  {
    key: 'pictureTop',
    defaultKey: 'defaultPictureTop',
    name: t('pages.campaignList.modal.campaignPictures.pictures.top'),
    upload: useSingleFileUpload<WbFile>(prevFileTop),
  },
  {
    key: 'pictureFront',
    defaultKey: 'defaultPictureFront',
    name: t('pages.campaignList.modal.campaignPictures.pictures.front'),
    upload: useSingleFileUpload<WbFile>(prevFileFront),
  },
  {
    key: 'pictureBack',
    defaultKey: 'defaultPictureBack',
    name: t('pages.campaignList.modal.campaignPictures.pictures.back'),
    upload: useSingleFileUpload<WbFile>(prevFileBack),
  },
]);

const rawCampaign = syncRef(internalCampaign, (campaign) => ({
  ...initialData(campaign),
}));

const unlockedPictures = computed(() =>
  pictures.filter(
    (pic) =>
      !rawCampaign.value.locked.pictures[
        pic.key as 'pictureLeft' | 'pictureRight' | 'pictureTop' | 'pictureFront' | 'pictureBack'
      ],
  ));

const lockedPictures = computed(() =>
  pictures.filter(
    (pic) =>
      rawCampaign.value.locked.pictures[
        pic.key as 'pictureLeft' | 'pictureRight' | 'pictureTop' | 'pictureFront' | 'pictureBack'
      ],
  ));

const {
  data: outputCampaign,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    region: string().nullable().required(t('validation.notEmpty')),
    active: boolean(),
    date: object({
      start: date()
        .when('locked.date', {
          is: false,
          then: date().required(t('validation.notEmpty')),
        })
        .nullable(),
      end: date()
        .when('locked.date', {
          is: false,
          then: date().required(t('validation.notEmpty')),
        })
        .nullable(),
    }).when('locked.date', {
      is: true,
      then: object().nullable(),
    }),
    translations: array(
      object({
        title: string().nullable(),
        language: string().nullable(),
      }),
    ),
    qualifiedDriversFilter: object().when('locked.qualifiedDriversFilter', {
      is: false,
      then: object().required(t('validation.notEmpty')),
    }),
    driversCount: number()
      .when('locked.driversCount', {
        is: false,
        then: number().required(t('validation.notEmpty')),
      })
      .nullable(),
  }),
  rawCampaign,
);

const fetchRegions = collOptionsProvider<Region>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
  }),
);

const resetFiles = () => {
  for (const picture of pictures) {
    picture.upload.reset();
  }
};

function resetData() {
  rawCampaign.value = { ...initialData() };
  setValidation(false);
  resetFiles();
  resetImages();
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalCampaign.value = undefined;
}

const [exec, { loading: saving }] = useFetchAction();

const loading = computed(() => saving.value || pictures.some((picture) => picture.upload.loading.value) || imagesSaving.value);

const driverFilter = ref<LogicGate>(null);

const validationFunction = (data: MaybeHydra<Filter>): boolean => {
  if (
    !rawCampaign.value.region
    || (rawCampaign.value.locked.date && (!props.campaignGroup?.defaultStartDate || !props.campaignGroup?.defaultEndDate))
    || (!rawCampaign.value.locked.date && (!rawCampaign.value.date.start || !rawCampaign.value.date.end))
  ) {
    return false;
  }
  return driverFilter.value?.validateData(data);
};

const save = validatedFn(async () => {
  if (
    !outputCampaign.value.qualifiedDriversFilter ||
    !driverFilter.value.validateData(outputCampaign.value.qualifiedDriversFilter)
  ) {
    return;
  }

  try {
    const getAdditionalImage = (fileId: string) =>
      props.campaign?.additionalPhotos.find((photo) => photo?.file?.id === fileId);

    const uploadFiles = await Promise.all(
      unlockedPictures.value.map(async ({ upload }) => upload.getUpdatedFiles(await upload.uploadDiff())?.[0]),
    );

    const additionalImages = await uploadImageDiff();
    const newImages = getUpdatedFiles(additionalImages);

    const edit = !!props.campaign;
    const url = edit ? props.campaign['@id'] : '/campaigns';

    if (!props.campaignGroup) {
      notify?.({
        variant: 'error',
        title: t('saveError'),
      });
      return;
    }

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        group: props.campaignGroup['@id'],
        startDate: outputCampaign.value.locked.date ? null : outputCampaign.value.date.start,
        endDate: outputCampaign.value.locked.date ? null : outputCampaign.value.date.end,
        driversCount: outputCampaign.value.locked.driversCount
          ? null
          : outputCampaign.value.driversCount,
        translations: (outputCampaign.value.translations as MaybeHydra<CampaignTranslations>[])
          .filter(
            (trans: MaybeHydra<CampaignTranslations>) =>
              (trans.text || trans.title) &&
              !outputCampaign.value.locked.translations[
                trans.language as 'en' | 'de' | 'fr' | 'it'
              ],
          )
          .map((trans) => ({ ...trans, id: trans['@id'] ?? undefined })),
        ...Object.fromEntries(
          unlockedPictures.value.map(({ key }, index) => [
            key,
            uploadFiles[index]?.file?.['@id'] ?? null,
          ]),
        ),
        additionalPhotos: newImages.map((image) => ({
          id: getAdditionalImage(image.file.id)?.['@id'],
          file: image.file['@id'],
        })),
        ...Object.fromEntries(lockedPictures.value.map(({ key }) => [key, null])),
        qualifiedDriversFilter: outputCampaign.value.locked.qualifiedDriversFilter
          ? null
          : parseFilter(outputCampaign.value.qualifiedDriversFilter),
        region: outputCampaign.value.region,
        active: outputCampaign.value.active,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('saveError'),
    });
  }
});
</script>

<style lang="scss" scoped>
::v-deep(.error) {
  aspect-ratio: 1;
}
</style>

